<template>
    <page-container maxSize="2xl">
        <div class="grid grid-cols-1 gap-6 xl:gap-x-12 xl:grid-cols-[auto_repeat(11,1fr)] xl:grid-rows-[auto_1fr]">
            <div class="order-2 xl:order-0 xl:row-span-2 xl:w-64">
                <analytics-blast-forecast-sidebar
                    v-model="fetchParams"
                    :orgUnitId="props.orgUnitId"
                    :groupId="props.groupId"
                    :showEstimatedCost="showEstimatedCost"
                    @dayClick="openDay($event)"
                    @reset="resetFilters()"
                ></analytics-blast-forecast-sidebar>
            </div>

            <div class="order-1 xl:order-0 xl:col-span-11">
                <h1 class="m-0 text-2xl sm:text-3xl font-extrabold text-slate-700 flex items-center gap-4">
                    <font-awesome-icon :icon="faWindsock" class="text-slate-500 w-8 h-8"></font-awesome-icon>
                    <span> Blast Forecast </span>
                </h1>
            </div>

            <div class="order-3 w-full relative xl:col-span-11">
                <transition
                    enter-from-class="opacity-0 -translate-y-4"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-4"
                >
                    <div v-if="loading" class="absolute w-full transition pt-20">
                        <loading-bars data-test="loading-bars"></loading-bars>
                    </div>
                </transition>

                <div
                    v-if="debouncedFetchParams"
                    data-test="chart-container"
                    class="flex flex-col gap-6"
                    :class="{
                        'opacity-0 pointer-events-none': loading,
                        'opacity-100 delay-200 transition duration-150': !loading,
                    }"
                >
                    <vx-tabs v-if="showEstimatedCost" v-model="currentTab">
                        <vx-tab value="messages">
                            <font-awesome-icon class="mr-1" :icon="faMessage"></font-awesome-icon> Estimated Messages
                        </vx-tab>
                        <vx-tab value="cost">
                            <font-awesome-icon class="mr-1" :icon="faDollar"></font-awesome-icon> Estimated Cost
                        </vx-tab>
                    </vx-tabs>
                    <blast-forecast-calendar
                        @dayClick="openDay"
                        :fetchParams="debouncedFetchParams"
                        :orgUnitId="props.orgUnitId"
                        :timezone="timezone"
                        :showEstimatedCost="showEstimatedCost"
                        :estimatedCost="showEstimatedCost && currentTab === 'cost'"
                    />
                    <blast-forecast-table
                        ref="table"
                        :fetchParams="debouncedFetchParams"
                        :orgUnitId="props.orgUnitId"
                        :timezone="timezone"
                        :showEstimatedCost="showEstimatedCost"
                        :estimatedCost="showEstimatedCost && currentTab === 'cost'"
                    />
                </div>
            </div>
        </div>
    </page-container>
</template>

<script lang="ts" setup>
import PageContainer from '~/components/general/PageContainer.vue';
import LoadingBars from '~/components/general/LoadingBars.vue';
import { useDebouncedFetchParams } from '~/components/analytics/useDebouncedFetchParams';
import { formatDateForRange } from '~/utils/analytics';
import { audienceTypes, statuses } from '~/constants/campaign';
import { SENDER_TYPES } from '~/constants/senderTypes';
import AnalyticsBlastForecastSidebar from './AnalyticsBlastForecastSidebar.vue';
import { VxTabs, VxTab } from '@voxie/frontend-components';
import dayjs from '~/utils/dayjs';
import { faDollar, faWindsock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { GetBlastForecastRequest } from '~/services/blastForecast/blastForecastClient.types';
import BlastForecastCalendar from './charts/BlastForecastCalendar.vue';
import BlastForecastTable from './charts/BlastForecastTable.vue';
import { getClientTz } from '~/utils/date';
import type { BlastForecastItemAggregated } from './composables/useBlastForecastSummary';
import { inject, onMounted, ref, useTemplateRef } from 'vue';
import type { Features } from '~/provides/features.types';
import { useMessageRules } from '~/components/quick-blast/useMessageRules';
import { faMessage } from '@fortawesome/pro-regular-svg-icons';
import { useCostVisibility } from '~/composables/useCostVisibility';

const FEATURES = inject('FEATURES') as Features;

const { showEstimatedCost } = useCostVisibility();

const timezone = getClientTz();

const defaultFilters = (): GetBlastForecastRequest => {
    const filters: GetBlastForecastRequest = {
        range: [
            formatDateForRange(dayjs().tz(getClientTz()).startOf('month')),
            formatDateForRange(dayjs().tz(getClientTz()).startOf('month').add(4, 'months')),
        ],
        audience_types: audienceTypes().map((audienceType) => audienceType.name),
        statuses: [statuses.DRAFT, statuses.SCHEDULED, statuses.PUBLISHED, statuses.COMPLETED],
        category: undefined,
        has_emoji: undefined,
        has_urls: undefined,
        has_variables: undefined,
        has_media: undefined,
        group_id: undefined,
        org_unit_id: undefined,
        modified_start_date: undefined,
        modified_end_date: undefined,
    };

    if (FEATURES.contact_groups) {
        filters.sender_types = SENDER_TYPES.map((senderType) => senderType.value);
    }

    return filters;
};

const { fetchParams, debouncedFetchParams, loading, resetFilters } = useDebouncedFetchParams<GetBlastForecastRequest>(
    defaultFilters()
);

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
});

const { getMessageRules } = useMessageRules();

onMounted(() => {
    if (props.orgUnitId) {
        getMessageRules({ orgUnitId: props.orgUnitId });
    }
});

const tableRef = useTemplateRef<InstanceType<typeof BlastForecastTable>>('table');

const openDay = (item: BlastForecastItemAggregated) => {
    tableRef.value?.openItem(item);
};

const currentTab = ref<'messages' | 'cost'>('messages');
</script>
